import { makeStyles, TextField } from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import { useField, useFormikContext } from 'formik'
import { filter, find, includes } from 'lodash'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { MdExpandMore } from 'react-icons/md'
import { useDebounce, useEffectOnce } from 'react-use'
import { FIELD_MODE } from '~/common/constants'
import { useFieldError, useFieldFocused } from '~/common/hooks'
import { useTranslation } from '@opus/web.core.hooks.use-translation'
import { LabelField } from '@opus/web.core.form.label-field'
import { parseLocation } from '~/common/helpers'
import CircularProgress from '@material-ui/core/CircularProgress'
import cityFieldStore from './city-field.store'

const useStyles = makeStyles((theme) => ({
	clearIndicator: {
		backgroundColor: theme.palette.primary.main,

		'&:hover': {
			backgroundColor: theme.palette.primary.main,
		},
		'& span': {
			'& svg': {
				fill: 'white',
				fontSize: '0.6rem',
			},
		},
	},
}))

export const CityField = ({ name, validate, label, placeholder, disabled, freeSolo, mode, multiple, disableClearable, onChange }) => {
	const { t } = useTranslation()
	const { setFieldValue } = useFormikContext()
	const [field, meta] = useField({ name, validate: mode === FIELD_MODE.edit && validate })

	const [inputValue, updateInputValue] = useState(field.value?.name)
	const [focused, focusProps] = useFieldFocused(field)
	const classes = useStyles()
	const [open, setOpen] = React.useState(false)
	const error = useFieldError(meta)

	const { fetchCities, options, loading } = cityFieldStore

	useEffectOnce(() => {
		void fetchCities()
	})

	useEffect(() => {
		if (!loading) {
			return undefined
		}

		void fetchCities(field.value?.name || '')
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [field?.value?.name])

	const handleChange = useCallback(
		(event, option) => {
			if (option !== null) {
				const address = parseLocation(option)
				setFieldValue(name, { ...address })
				onChange && onChange(event, option)
				updateInputValue(multiple ? option?.map((opt) => opt.name) : option?.name)
			} else {
				setFieldValue(name, {})
				onChange && onChange(event, option)
				updateInputValue(undefined)
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[updateInputValue, multiple, setFieldValue, name]
	)

	useDebounce(
		() => {
			if (field.value?.name !== inputValue && !focused) {
				updateInputValue(field.value?.name || (multiple ? [] : ''))
			}
		},
		300,
		[field.value]
	)

	const selectedOption = useMemo(() => {
		if (multiple) {
			return filter(options, (option) => includes(field.value, option.name)) || []
		}

		return find(options, (option) => option.fullAddress === field?.value?.fullAddress)
	}, [options, field.value, multiple])

	const viewValue = useMemo(() => (multiple ? selectedOption?.map((opt) => opt.name)?.join(', ') : selectedOption?.name), [multiple, selectedOption])

	if (mode === FIELD_MODE.view) {
		return <LabelField label={t(label)} displayValueFormat={() => viewValue || field.value} />
	}

	return (
		<Autocomplete
			id={name}
			freeSolo={freeSolo}
			open={open}
			onOpen={() => {
				setOpen(true)
			}}
			onClose={() => {
				setOpen(false)
			}}
			classes={{
				clearIndicatorDirty: classes.clearIndicator,
			}}
			loading={loading}
			options={options}
			getOptionSelected={(option, value) => {
				return option.fullAddress === value.fullAddress
			}}
			renderOption={(option) => (
				<>
					<b>{option.name}</b>
					{', ' + option.stateCode + ', ' + option.countryCode}
				</>
			)}
			getOptionLabel={(option) => {
				return option.name || ''
			}}
			fullWidth
			multiple={multiple}
			{...field}
			{...focusProps}
			disabled={disabled}
			onChange={handleChange}
			inputValue={inputValue}
			disableClearable={disableClearable}
			filterOptions={(options) => {
				return options
			}}
			onInputChange={(event, newInputValue) => {
				updateInputValue(newInputValue)
				fetchCities(newInputValue)
			}}
			popupIcon={<MdExpandMore />}
			renderInput={(params) => {
				return (
					<TextField
						{...params}
						label={t(label)}
						disabled={disabled}
						placeholder={placeholder && t(placeholder)}
						error={!!error}
						helperText={error}
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<React.Fragment>
									{loading ? <CircularProgress color="inherit" size={20} /> : null}
									{params.InputProps.endAdornment}
								</React.Fragment>
							),
						}}
					/>
				)
			}}
		/>
	)
}

CityField.defaultProps = {
	freeSolo: false,
	mode: FIELD_MODE.edit,
	multiple: false,
}
